import React from "react";
import { Grid } from "components/src/components/Grid/Grid";
import { Icon } from "components/src/components/Icon/Icon";
import cx from "components/src/utils/cx";

export interface MarketLabelProps {
  label: string | { text: string; hasSGP?: boolean };
  index: number;
  isOddsBoost?: boolean;
  isCompact?: boolean;
  textSize?: string;
  textColor?: string;
}

interface ComputedLabel {
  hasWhiteSpace: boolean;
  hasSGP: boolean;
  label: string;
}

const computeLabel = (label: MarketLabelProps["label"]): ComputedLabel => {
  if (typeof label === "string" && label) {
    const hasSGP = /^\[SGP\]/.test(label);
    return {
      hasWhiteSpace: /\s/.test(label),
      hasSGP,
      label: hasSGP ? label.replace(/^\[SGP\]/, "").trim() : label.trim(),
    };
  }
  
  if (typeof label === "object" && "text" in label && label.text) {
    return {
      hasWhiteSpace: /\s/.test(label.text),
      hasSGP: label?.hasSGP || false,
      label: label.text.trim(),
    };
  }

  return { hasWhiteSpace: true, hasSGP: false, label: "" };
};

export const MarketLabel = ({ label, index, isOddsBoost, isCompact, textSize, textColor }: MarketLabelProps) => {
  const computed = computeLabel(label);
  const _textSize = textSize ?? "heading-xs";
  const _textColor = textColor ?? "cui-text-fg-moderate";

  if (isCompact) {
    return (
      <Grid.Cell colSpan={2} items="center">
        <div className={`${_textSize} ${_textColor} cui-text-center cui-w-full`}>
          {computed.hasSGP && (
            <Icon name="ui_sgp" size="sm" className="cui-mr-1 cui-align-text-top" color="fg-primary-strong" />
          )}
          <span>{computed.label}</span>
        </div>
      </Grid.Cell>
    );
  }

  return (
    <Grid.Cell
      colSpan={isOddsBoost ? 3 : 2}
      items="center"
      className=""
      key={`mh-${label}-${index}`}
    >
      <div className={cx(
        {
          "cui-break-words": computed.hasWhiteSpace,
          "cui-truncate": !computed.hasWhiteSpace,
        },
        `${_textSize} ${_textColor} cui-text-center cui-w-full cui-inline`
      )}>
        {computed.hasSGP && (
          <Icon name="ui_sgp" size="sm" className="cui-mr-1 cui-align-text-top" color="fg-primary-strong" />
        )}
        <span>{computed.label}</span>
      </div>
    </Grid.Cell>
  );
};
